import { Select, TextInput } from '@ally/forms'
import { Box, upToMd } from '@ally/metronome-ui'
import React from 'react'
import {
  formatSSN,
  SSN_WHITELIST_PATTERN,
  SSN_BLACKLIST_PATTERN,
  formatTIN,
  TIN_LOOSE_PATTERN,
} from '@ally/aos-utils'
import { maskSSN, maskTaxId } from '../../utils/formatter'

interface BeneIdentificationFieldProps {
  beneficiaryClassification: string
  changeBeneIdentificationFieldValue: (
    field: string,
    value: string | undefined,
  ) => void
  onBeneIdentificationChange: (e: React.BaseSyntheticEvent) => void
  ssnEditDisabled?: boolean
  maskedSsn: string
  maskedTaxId: string
  identificationNumber: string
  beneIdentification: string
  setStepTwoFormErrorCode?: React.Dispatch<React.SetStateAction<string>>
  isIraPage?: boolean
}

export const BeneficiarySsnTaxId: React.FC<BeneIdentificationFieldProps> = ({
  maskedSsn,
  maskedTaxId,
  identificationNumber,
  beneIdentification,
  beneficiaryClassification,
  changeBeneIdentificationFieldValue,
  onBeneIdentificationChange,
  ssnEditDisabled,
  setStepTwoFormErrorCode,
  isIraPage,
}) => {
  // istanbul ignore next
  const onSsnChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    if (setStepTwoFormErrorCode) {
      setStepTwoFormErrorCode('')
    }
    const numbersOnly = /[^0-9 -]/g
    if (!numbersOnly.test(value)) {
      changeBeneIdentificationFieldValue('maskedSsn', value)
    }
  }

  // istanbul ignore next - setting the form value
  const onTaxIdChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    const numbersOnly = /[^0-9 -]/g
    if (!numbersOnly.test(value)) {
      changeBeneIdentificationFieldValue('maskedTaxId', value)
    }
  }

  // istanbul ignore next
  const onSsnFocus = ({ target }: React.BaseSyntheticEvent): void => {
    if (maskedSsn && maskedSsn.length === 11 && identificationNumber) {
      changeBeneIdentificationFieldValue('maskedSsn', identificationNumber)
      requestAnimationFrame(() => target.select())
    }
  }

  // istanbul ignore next - field selection / clearing
  const onTaxIdFocus = ({ target }: React.BaseSyntheticEvent): void => {
    if (maskedTaxId && maskedTaxId.length === 10 && identificationNumber) {
      changeBeneIdentificationFieldValue('maskedTaxId', identificationNumber)
      requestAnimationFrame(() => target.select())
    }
  }

  // istanbul ignore next
  const onSsnBlur = ({ target: { value } }: React.BaseSyntheticEvent): void => {
    if (maskedSsn && (maskedSsn.length === 9 || maskedSsn.length === 11)) {
      const formatted = formatSSN(value)
      changeBeneIdentificationFieldValue('identificationNumber', formatted)
      const ssn = maskSSN(formatted)
      changeBeneIdentificationFieldValue('maskedSsn', ssn)
    }
  }

  // istanbul ignore next - setting the form value if value valid
  const onTaxIdBlur = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    if (
      maskedTaxId &&
      (maskedTaxId.length === 9 || maskedTaxId.length === 10)
    ) {
      const formatted = formatTIN(value)
      changeBeneIdentificationFieldValue('identificationNumber', formatted)
      const taxId = maskTaxId(formatted)
      changeBeneIdentificationFieldValue('maskedTaxId', taxId)
    }
  }

  const options =
    beneficiaryClassification === 'trust'
      ? [
          { label: 'Social Security Number', value: 'SocialSecurityNumber' },
          { label: 'Tax ID', value: 'TaxId' },
        ]
      : [
          {
            label: 'Social Security Number / Tax ID',
            value: 'SocialSecurityNumber',
          },
          {
            label: 'Alien Identification Card',
            value: 'AlienIdentificationCard',
          },
          {
            label: `Driver's License`,
            value: `DriversLicense`,
          },
          {
            label: 'Valid Passport',
            value: 'ValidPassport',
          },
          {
            label: 'Military ID',
            value: 'MilitaryId',
          },
        ]

  const validateIdentification = (val: string): string => {
    if (!val) {
      return 'Make a selection.'
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateSsn = (val: string): string => {
    // istanbul ignore next - related to field masking
    if (val && val.indexOf('\u2022') !== -1) {
      return ''
    }
    if (
      !val ||
      (val.length !== 9 && val.length !== 11) ||
      !SSN_WHITELIST_PATTERN.test(formatSSN(val)) ||
      SSN_BLACKLIST_PATTERN.test(val)
    ) {
      return 'Enter a valid Social Security Number.'
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateTaxId = (val: string): string => {
    // istanbul ignore next - related to field masking
    if (val && val.indexOf('\u2022') !== -1) {
      return ''
    }
    if (
      !val ||
      (val.length !== 9 && val.length !== 10) ||
      !TIN_LOOSE_PATTERN.test(formatTIN(val))
    ) {
      return 'Enter a valid Tax ID.'
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  return (
    <Box mb="md">
      {isIraPage && (
        <Box width={upToMd('88%', '50%')} mt="md">
          <TextInput
            isPrivate
            labelContent="Social Security Number"
            name="maskedSsn"
            inputType="text"
            validate={validateSsn}
            placeholder="•••-••-••••"
            onChange={onSsnChange}
            onFocus={onSsnFocus}
            onBlur={onSsnBlur}
            required
            data-testid="ssn-input-field"
            maxLength={11}
            disabled={ssnEditDisabled}
          />
        </Box>
      )}

      {(beneficiaryClassification.toLowerCase() === 'individual' ||
        beneficiaryClassification.toLowerCase() === 'trust') && (
        <Box width={upToMd('88%', '50%')}>
          <Select
            name="beneIdentification"
            options={options}
            placeholder="Select"
            label="Beneficiary Identification"
            validate={validateIdentification}
            onChange={onBeneIdentificationChange}
            data-testid="beneficiary-identification-select"
          />
        </Box>
      )}
      {beneIdentification === 'SocialSecurityNumber' && (
        <Box width={upToMd('88%', '50%')} mt="md">
          <TextInput
            isPrivate
            labelContent="Social Security Number"
            name="maskedSsn"
            inputType="text"
            validate={validateSsn}
            placeholder="•••-••-••••"
            onChange={onSsnChange}
            onFocus={onSsnFocus}
            onBlur={onSsnBlur}
            required
            data-testid="ssn-input-field"
            maxLength={11}
            disabled={ssnEditDisabled}
          />
        </Box>
      )}
      {(beneficiaryClassification.toLowerCase() === 'trust' &&
        beneIdentification === 'TaxId') ||
      beneficiaryClassification.toLowerCase() === 'charity' ||
      beneficiaryClassification.toLowerCase() === 'other' ? (
        <Box width={upToMd('88%', '50%')} mt="md">
          <TextInput
            isPrivate
            labelContent="Tax ID"
            name="maskedTaxId"
            inputType="text"
            validate={validateTaxId}
            placeholder="••-•••••••"
            onChange={onTaxIdChange}
            onFocus={onTaxIdFocus}
            onBlur={onTaxIdBlur}
            required
            data-testid="taxid-input-field"
            maxLength={10}
          />
        </Box>
      ) : (
        <></>
      )}
    </Box>
  )
}
